import { Spacing } from '@persona-id/design-tokens';
import { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';

import ImageCar from 'images/verify/id-class/car.svg';
import ImageFlag from 'images/verify/id-class/flag.svg';
import ImageGovernment from 'images/verify/id-class/government.svg';
import ImagePlane from 'images/verify/id-class/plane.svg';
import Button, { ButtonType } from 'inquiry/components/Button';
import VerticalOption from 'inquiry/components/VerticalOption';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { GovernmentIdClass } from 'lib/constants/governmentIds';
import { CountryCode } from 'lib/constants/locales';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import { IconStyle } from 'lib/interfaces/ThemeSerialized';

const ThemedIcon = styled('span')`
  rect[fill]:not([fill='none']) {
    fill: ${({ theme }) => theme.iconColorGovernmentIdType};
  }

  path[fill]:not([fill='none']) {
    fill: ${({ theme }) => theme.colorModalBackground};
  }

  height: 32px;
  margin-right: ${Spacing[4]};
`;

const OptionVerticalButton = styled(Button)`
  width: 100%;
  & + & {
    margin-top: ${Spacing[5]};
  }
`;

interface OptionVerticalProps {
  buttonType: 'outline' | 'outline_light' | 'button';
  countryCode?: CountryCode;
  handleSelectType: (type: string) => void;
  governmentIdClass: GovernmentIdClass;
  localizations: StepGovernmentIdConfig['localizations']['select-page'];
}

export default function OptionVertical({
  buttonType,
  countryCode,
  handleSelectType,
  governmentIdClass,
  localizations,
}: OptionVerticalProps) {
  const { iconStyle } = useContext(ThemeContext);
  const { buttons: buttonStyles } = useStepGovernmentIdStyleContext();

  const childProps = {
    id: `select__option--${governmentIdClass}`,
    'data-test': 'option-block',
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    onClick: () => handleSelectType(governmentIdClass),
  };

  if (buttonType === 'button') {
    return (
      <OptionVerticalButton
        {...childProps}
        styles={buttonStyles?.[ButtonType.Primary]}
        type='button'
      >
        {textForGovernmentId(localizations, countryCode, governmentIdClass)}
      </OptionVerticalButton>
    );
  }

  return (
    <VerticalOption
      {...childProps}
      buttonType={buttonType}
      icon={
        iconStyle !== IconStyle.None && (
          <ThemedIcon data-test='option-vertical__pictograph' aria-hidden>
            <OptionIcon governmentIdClass={governmentIdClass} />
          </ThemedIcon>
        )
      }
    >
      {textForGovernmentId(localizations, countryCode, governmentIdClass)}
    </VerticalOption>
  );
}

const OptionIcon = ({ governmentIdClass }: Pick<OptionVerticalProps, 'governmentIdClass'>) => {
  switch (governmentIdClass) {
    case 'dl':
      return <ImageCar />;

    case 'ofw':
    case 'pp':
    case 'ppc':
    case 'visa':
      return <ImagePlane />;

    case 'id':
      return <ImageFlag />;

    case 'rp':
    case 'pr':
    default:
      return <ImageGovernment />;
  }
};

/**
 * If country code not passed, check for unscoped translation for ID class.
 *
 * If country code is passed, first check for country code scoped translation,
 * and fall back to unscoped.
 *
 * This is relevant for ca-id, us-id and ng-id for now. We may need to support other cases
 * of {country}-{idclass} in the future.
 */
function textForGovernmentId(
  localizations: StepGovernmentIdConfig['localizations']['select-page'],
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  countryCode: CountryCode = undefined,
  governmentIdClass: GovernmentIdClass,
) {
  const textWithoutCountryScoping = localizations[governmentIdClass as keyof typeof localizations];

  if (countryCode == null) {
    return textWithoutCountryScoping;
  } else {
    const textWithCountryScoping =
      localizations[
        `${countryCode.toLowerCase()}-${governmentIdClass}` as keyof typeof localizations
      ];

    return textWithCountryScoping ?? textWithoutCountryScoping;
  }
}
